// apps/client/src/assets/builders/fossil.ts (M2-T09) — `code:fossil`: identified fossils are carved stone plaques with a bone relief
// extruded from a silhouette (one per FossilEntry.silhouette); `fossil.unknown` is a rough lump with a hint of bone. Silhouettes are
// drawn in a unit square centred on 0, y up; overlapping polygons read as one carved shape.
// With `display: 'skeleton'` (the museum's plinths) a part of a multi-part set is its share of one chunky, rounded skeleton standing
// on the plinth (v1 round 4: flat extruded cut-outs read as small paper shapes on a phone). Each set is drawn whole in its plinth's
// frame (SKELETONS): x along the plinth (skull to +x), y up from the plinth top, z across it; a part's model is that frame moved so
// its origin is the centre of its own tile on the plinth top (`skeletonSlot`), where the museum stands it.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { getEntry, itemsOfKind, type FossilEntry } from '@whiskerton/content';
import { UNKNOWN_FOSSIL } from '@whiskerton/shared/rules/museum';
import { toyMaterial } from '../../characters/doll';
import type { CodeBuilder } from '../service';

export type Poly = [number, number][];
const STONE = '#CDBFA6', BONE = '#F3EBD9', ROCK = '#B9AE98';
const ell = (cx: number, cy: number, rx: number, ry: number, n = 18): Poly =>
  Array.from({ length: n }, (_, i) => { const a = (i / n) * Math.PI * 2; return [cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]; });
const rect = (x: number, y: number, w: number, h: number): Poly => [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
const tri = (a: [number, number], b: [number, number], c: [number, number]): Poly => [a, b, c];
const ribs = (n: number, x0: number, x1: number, y: number, h: number): Poly[] =>
  Array.from({ length: n }, (_, i) => rect(x0 + ((x1 - x0) * i) / n, y - h / 2, ((x1 - x0) / n) * 0.45, h));
const taper = (x0: number, x1: number, h0: number, h1: number): Poly => [[x0, -h0 / 2], [x1, -h1 / 2], [x1, h1 / 2], [x0, h0 / 2]];
function spiralBand(turns: number, r0: number, r1: number, width: number): Poly {
  const n = 64, outer: Poly = [], inner: Poly = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, a = t * turns * Math.PI * 2, r = r0 + (r1 - r0) * t;
    outer.push([Math.cos(a) * (r + width / 2), Math.sin(a) * (r + width / 2)]);
    inner.push([Math.cos(a) * Math.max(0.005, r - width / 2), Math.sin(a) * Math.max(0.005, r - width / 2)]);
  }
  return [...outer, ...inner.reverse()];
}

export const SILHOUETTES: Readonly<Record<string, () => Poly[]>> = {
  blob: () => [ell(0, 0, 0.3, 0.22, 12)],
  skull_horned: () => [ell(0.02, 0, 0.26, 0.17), ell(-0.24, 0.06, 0.12, 0.26), tri([0.06, 0.12], [0.36, 0.36], [0.14, 0.08]), tri([-0.04, 0.14], [0.2, 0.42], [0.04, 0.1]), tri([0.24, -0.02], [0.44, 0.04], [0.26, 0.06])],
  skull_rex: () => [ell(-0.02, 0.04, 0.34, 0.16), rect(-0.3, -0.2, 0.58, 0.08), ...[0, 1, 2, 3, 4].map((i) => tri([-0.24 + i * 0.11, -0.12], [-0.2 + i * 0.11, -0.2], [-0.16 + i * 0.11, -0.12]))],
  skull_plated: () => [ell(0, 0, 0.26, 0.14), tri([-0.2, 0.08], [-0.05, 0.3], [0.05, 0.1])],
  skull_crest: () => [ell(0, 0, 0.14, 0.12), tri([0.1, 0.04], [0.46, 0.02], [0.1, -0.06]), tri([-0.08, 0.04], [-0.46, 0.26], [-0.04, 0.12])],
  skull_tusk: () => [ell(0, 0.08, 0.24, 0.24), ...[-1, 1].map((s) => [[s * 0.08, -0.08], [s * 0.3, -0.28], [s * 0.4, -0.1], [s * 0.34, -0.16], [s * 0.14, -0.04]] as Poly)],
  skull_saber: () => [ell(0, 0.06, 0.24, 0.16), rect(0.06, -0.3, 0.05, 0.26), rect(-0.11, -0.3, 0.05, 0.26)],
  torso_ribs: () => [rect(-0.42, 0.12, 0.84, 0.05), ...ribs(8, -0.36, 0.36, -0.02, 0.26)],
  torso_plates: () => [rect(-0.42, -0.04, 0.84, 0.05), ...ribs(7, -0.34, 0.34, -0.14, 0.2), ...[0, 1, 2, 3, 4].map((i) => tri([-0.3 + i * 0.14, 0.01], [-0.23 + i * 0.14, 0.32], [-0.16 + i * 0.14, 0.01]))],
  tail_long: () => [taper(-0.45, 0.45, 0.16, 0.02), ...ribs(9, -0.4, 0.35, 0, 0.12)],
  tail_spiked: () => [taper(-0.45, 0.4, 0.14, 0.04), tri([0.2, 0.04], [0.42, 0.3], [0.3, 0.04]), tri([0.28, -0.04], [0.46, -0.28], [0.36, -0.03])],
  wing: () => [[[-0.45, 0.05], [0.0, 0.3], [0.45, 0.38], [0.2, 0.0], [0.35, -0.25], [-0.1, -0.05]]],
  spiral: () => [spiralBand(2.4, 0.03, 0.34, 0.07)],
  trilobite: () => [ell(0, 0, 0.2, 0.36), ...[-0.24, -0.12, 0, 0.12, 0.24].map((y) => rect(-0.26, y - 0.02, 0.52, 0.04))],
  amber: () => [ell(0, 0, 0.26, 0.32, 9), ell(0.02, 0.02, 0.05, 0.08)],
  egg: () => [ell(0, -0.02, 0.22, 0.3, 24)],
  footprint: () => [ell(0, -0.12, 0.14, 0.16), ell(-0.16, 0.14, 0.06, 0.16), ell(0, 0.22, 0.06, 0.17), ell(0.16, 0.14, 0.06, 0.16)],
  coprolite: () => [ell(0, 0, 0.32, 0.13), ell(0.12, 0.08, 0.1, 0.07), ell(-0.14, 0.06, 0.09, 0.06)],
  tooth: () => [[[-0.16, 0.3], [0.16, 0.3], [0.02, -0.34]]],
  bird: () => [ell(0, 0, 0.12, 0.1), tri([-0.05, 0.05], [-0.42, 0.34], [0.08, 0.1]), tri([0.05, 0.05], [0.4, 0.3], [0.0, 0.12]), taper(0.08, 0.4, 0.06, 0.14)],
  fishbone: () => [ell(-0.3, 0, 0.14, 0.12), rect(-0.18, -0.02, 0.56, 0.04), ...ribs(6, -0.14, 0.3, 0, 0.26), tri([0.36, 0], [0.48, 0.14], [0.48, -0.14])],
  scorpion: () => [ell(-0.1, -0.05, 0.2, 0.12), ...[0, 1, 2, 3].map((i) => ell(0.12 + i * 0.07, 0.02 + i * 0.07, 0.05, 0.045)), tri([0.34, 0.26], [0.42, 0.36], [0.28, 0.34])],
  fern: () => [rect(-0.015, -0.4, 0.03, 0.8), ...[-0.3, -0.18, -0.06, 0.06, 0.18, 0.3].flatMap((y) => [ell(-0.11, y, 0.1, 0.035), ell(0.11, y, 0.1, 0.035)])],
};

// ---------- skeletons (display: 'skeleton')
type V = readonly [number, number, number];
const SOCKET = '#6E5A48', POST = '#6E5A48', MEMBRANE = '#E2D2B4';
const UP = new THREE.Vector3(0, 1, 0);
/** Chunky low-poly bone primitives, each its own mesh (the museum bakes the hall into a few draws). */
class Bones {
  readonly g = new THREE.Group();
  private add(geo: THREE.BufferGeometry, colour = BONE): THREE.Mesh {
    const m = new THREE.Mesh(geo, toyMaterial(colour));
    m.castShadow = m.receiveShadow = true;
    this.g.add(m);
    return m;
  }
  /** A rounded bone from a to b. */
  bone(a: V, b: V, r: number, colour = BONE): this {
    const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b), d = B.clone().sub(A);
    const m = this.add(new THREE.CapsuleGeometry(r, Math.max(0.001, d.length()), 2, 8), colour);
    m.position.copy(A).add(B).multiplyScalar(0.5);
    m.quaternion.setFromUnitVectors(UP, d.normalize());
    return this;
  }
  /** A spine through `pts`, thinning from r0 to r1, with a knob at every joint. */
  spine(pts: readonly V[], r0: number, r1: number): this {
    for (let i = 0; i + 1 < pts.length; i++) {
      const r = r0 + ((r1 - r0) * i) / Math.max(1, pts.length - 2);
      this.bone(pts[i]!, pts[i + 1]!, r).ball(pts[i + 1]!, r * 1.3);
    }
    return this.ball(pts[0]!, r0 * 1.2);
  }
  ball(c: V, r: number | V, colour = BONE): this {
    const m = this.add(new THREE.SphereGeometry(1, 10, 7), colour);
    m.position.set(...c);
    if (typeof r === 'number') m.scale.setScalar(r); else m.scale.set(...r);
    return this;
  }
  /** A horn or spike from its base to its tip. */
  cone(base: V, tip: V, r: number): this {
    const A = new THREE.Vector3(...base), B = new THREE.Vector3(...tip), d = B.clone().sub(A);
    const m = this.add(new THREE.ConeGeometry(r, d.length(), 8, 1));
    m.position.copy(A).add(B).multiplyScalar(0.5);
    m.quaternion.setFromUnitVectors(UP, d.normalize());
    return this;
  }
  /** A rib: a hoop round the body (across z), open underneath. */
  rib(x: number, y: number, R: number, tube: number): this {
    const arc = Math.PI * 1.3, geo = new THREE.TorusGeometry(R, tube, 5, 10, arc);
    geo.rotateZ(Math.PI / 2 - arc / 2).rotateY(Math.PI / 2).translate(x, y, 0);
    this.add(geo);
    return this;
  }
  /** A curved bone (a tusk): an arc of radius R about c in the x-y plane, from angle a0 through `arc` (radians), at depth z. */
  arc(c: V, R: number, tube: number, a0: number, arc: number): this {
    const geo = new THREE.TorusGeometry(R, tube, 6, 12, arc);
    geo.rotateZ(a0).translate(...c);
    this.add(geo);
    return this;
  }
  /** A leg (or arm) through its joints, with a round foot. */
  leg(joints: readonly V[], r: number): this {
    const last = joints.length - 1, end = joints[last]!, foot = end[1] < 0.15;      // a leg down to the plinth stands on its foot
    const js = joints.map((p, i): V => (i === last && foot ? [p[0], r, p[2]] : p));
    for (let i = 0; i < last; i++) this.bone(js[i]!, js[i + 1]!, r * (1 - i * 0.12)).ball(js[i]!, r * 1.25);
    return foot ? this.ball([end[0] + 0.03, r * 0.8, end[2]], [r * 1.7, r * 0.8, r * 1.4]) : this.ball(js[last]!, r * 0.9);
  }
  /** A thick round plate (a frill) centred on c, its face tilted from upright by `tilt` radians about z. */
  disc(c: V, r: number, thick: number, tilt: number): this {
    this.add(new THREE.CylinderGeometry(r, r, thick, 14).rotateZ(tilt).translate(...c));
    return this;
  }
  /** A display post from the plinth top up to y. */
  post(x: number, y: number): this { this.add(new THREE.CylinderGeometry(0.035, 0.035, y, 8).translate(x, y / 2, 0), POST); return this; }
  eyes(x: number, y: number, z: number, r: number): this { return this.ball([x, y, z], r, SOCKET).ball([x, y, -z], r, SOCKET); }
  /** Both sides at once (z and −z). */
  pair(f: (s: 1 | -1) => void): this { f(1); f(-1); return this; }
}
type Part = FossilEntry['part'];
/** Each multi-part set, drawn whole (see the header): every part adds its own bones. Proportions are the toy's, not a textbook's: big
 *  heads, thick bones and one shape per animal you can name from across the hall (the frill and horns, the big-jawed biped, the back
 *  plates, the crest and wings, the tusks). */
export const SKELETONS: Readonly<Record<string, Partial<Record<Part, (b: Bones) => void>>>> = {
  trike: {
    tail: (b) => { b.spine([[0.08, 0.3, 0], [0.42, 0.5, 0], [0.8, 0.68, 0], [1.15, 0.78, 0]], 0.04, 0.085); b.post(0.45, 0.48); },
    torso: (b) => {
      b.spine([[1.15, 0.78, 0], [1.45, 0.88, 0], [1.75, 0.88, 0], [2.05, 0.8, 0]], 0.095, 0.09).ball([1.2, 0.76, 0], [0.17, 0.13, 0.19]);
      for (const x of [1.35, 1.5, 1.65, 1.8, 1.95]) b.rib(x, 0.66, 0.23, 0.048);
      b.pair((s) => { b.leg([[1.22, 0.72, s * 0.17], [1.34, 0.38, s * 0.2], [1.24, 0.06, s * 0.2]], 0.075); b.leg([[1.98, 0.66, s * 0.18], [1.9, 0.36, s * 0.21], [1.98, 0.06, s * 0.21]], 0.07); });
    },
    skull: (b) => {
      b.bone([2.05, 0.8, 0], [2.3, 0.84, 0], 0.085).ball([2.55, 0.84, 0], [0.24, 0.17, 0.17]);
      // The frill: a thick round shield sweeping up and back from the skull, knobbed along its rim.
      b.disc([2.3, 1.1, 0], 0.4, 0.08, -(Math.PI / 2 - 0.55));
      const u = [-Math.sin(0.55), Math.cos(0.55)] as const;
      for (let i = 0; i <= 6; i++) { const a = -Math.PI / 2 + (i / 6) * Math.PI; b.ball([2.3 + 0.4 * Math.cos(a) * u[0], 1.1 + 0.4 * Math.cos(a) * u[1], 0.4 * Math.sin(a)], 0.06); }
      b.cone([2.74, 0.8, 0], [2.96, 0.66, 0], 0.1)
        .pair((s) => { b.cone([2.52, 0.96, s * 0.09], [2.88, 1.26, s * 0.12], 0.055); })
        .cone([2.74, 0.88, 0], [2.84, 1.04, 0], 0.04).eyes(2.66, 0.9, 0.13, 0.045);
    },
  },
  rex: {
    tail: (b) => { b.spine([[0.05, 0.38, 0], [0.45, 0.62, 0], [0.85, 0.84, 0], [1.22, 0.98, 0]], 0.04, 0.09); b.post(0.55, 0.62); },
    torso: (b) => {
      b.spine([[1.22, 0.98, 0], [1.55, 1.05, 0], [1.88, 1.14, 0], [2.15, 1.24, 0]], 0.1, 0.09).ball([1.28, 0.95, 0], [0.18, 0.14, 0.17]);
      for (const x of [1.45, 1.62, 1.79, 1.96]) b.rib(x, 0.86 + (x - 1.45) * 0.3, 0.21, 0.048);
      b.pair((s) => {
        b.leg([[1.3, 0.9, s * 0.15], [1.5, 0.52, s * 0.17], [1.32, 0.18, s * 0.17], [1.5, 0.06, s * 0.17]], 0.085);
        b.leg([[2.05, 1.05, s * 0.13], [2.16, 0.92, s * 0.15], [2.24, 0.94, s * 0.15]], 0.035);     // the tiny arms
      });
    },
    skull: (b) => {
      b.bone([2.15, 1.24, 0], [2.34, 1.34, 0], 0.09).ball([2.5, 1.38, 0], [0.22, 0.17, 0.16]).ball([2.74, 1.3, 0], [0.2, 0.11, 0.13])
        .bone([2.36, 1.16, 0], [2.86, 1.12, 0], 0.06).eyes(2.52, 1.44, 0.13, 0.05);
      for (const x of [2.62, 2.72, 2.82]) b.pair((s) => { b.cone([x, 1.21, s * 0.08], [x + 0.01, 1.12, s * 0.08], 0.022); });
    },
  },
  stego: {
    tail: (b) => {
      b.spine([[0.08, 0.45, 0], [0.42, 0.6, 0], [0.8, 0.78, 0], [1.15, 0.95, 0]], 0.035, 0.08); b.post(0.6, 0.64);
      b.pair((s) => { b.cone([0.14, 0.48, s * 0.04], [0.02, 0.74, s * 0.3], 0.04).cone([0.32, 0.56, s * 0.04], [0.26, 0.86, s * 0.3], 0.04); });
      for (const [x, y, h] of [[0.7, 0.92, 0.12], [0.98, 1.06, 0.15]] as const) b.ball([x, y, 0], [0.09, h, 0.03]);
    },
    torso: (b) => {
      b.spine([[1.15, 0.95, 0], [1.5, 1.06, 0], [1.85, 0.96, 0], [2.15, 0.74, 0]], 0.09, 0.08).ball([1.25, 0.92, 0], [0.17, 0.13, 0.19]);
      for (const x of [1.38, 1.54, 1.7, 1.86]) b.rib(x, 0.84 - (x - 1.38) * 0.2, 0.23, 0.048);
      for (const [i, x] of [1.22, 1.42, 1.62, 1.82, 2.0].entries()) {
        const y = 1.06 - Math.abs(x - 1.5) * 0.35, h = 0.24 - Math.abs(x - 1.55) * 0.12;
        b.ball([x, y + h * 0.75, i % 2 ? 0.04 : -0.04], [0.13, h, 0.035]);
      }
      b.pair((s) => { b.leg([[1.3, 0.9, s * 0.17], [1.4, 0.48, s * 0.2], [1.3, 0.06, s * 0.2]], 0.075); b.leg([[2.0, 0.7, s * 0.17], [1.95, 0.38, s * 0.2], [2.0, 0.06, s * 0.2]], 0.07); });
    },
    skull: (b) => {
      b.bone([2.15, 0.74, 0], [2.38, 0.6, 0], 0.07).ball([2.52, 0.56, 0], [0.17, 0.1, 0.11]).cone([2.64, 0.54, 0], [2.82, 0.46, 0], 0.07)
        .eyes(2.55, 0.6, 0.09, 0.035);
    },
  },
  ptera: {
    wing: (b) => {
      b.post(0.9, 0.95).bone([0.72, 1.0, 0], [1.05, 1.08, 0], 0.085).rib(0.85, 0.95, 0.12, 0.03).rib(0.96, 0.97, 0.12, 0.03)
        .pair((s) => { b.bone([0.74, 0.98, s * 0.06], [0.56, 0.8, s * 0.08], 0.03); });
      for (const [s, lift] of [[1, 0.1], [-1, 0]] as const) {                // the arm, the long wing finger, and the wing between them
        b.leg([[1.0, 1.08, s * 0.12], [0.78, 1.34 + lift, s * 0.2], [0.5, 1.5 + lift, s * 0.24]], 0.06);
        b.bone([0.5, 1.5 + lift, s * 0.24], [0.02, 1.08 + lift, s * 0.26], 0.045).ball([0.02, 1.08 + lift, s * 0.26], 0.05);
        b.bone([0.5, 1.5 + lift, s * 0.24], [0.28, 1.62 + lift, s * 0.25], 0.03);
        b.ball([0.52, 1.28 + lift, s * 0.22], [0.42, 0.17, 0.025], MEMBRANE);
      }
    },
    skull: (b) => {
      b.bone([1.05, 1.08, 0], [1.26, 1.22, 0], 0.06).ball([1.36, 1.25, 0], [0.13, 0.1, 0.1])
        .cone([1.44, 1.22, 0], [1.98, 1.1, 0], 0.07).cone([1.3, 1.3, 0], [0.98, 1.5, 0], 0.06).eyes(1.38, 1.29, 0.08, 0.035);
    },
  },
  mammoth: {
    torso: (b) => {
      b.spine([[0.05, 0.92, 0], [0.35, 1.1, 0], [0.7, 1.16, 0], [1.02, 1.1, 0]], 0.1, 0.1).bone([0.05, 0.92, 0], [-0.04, 0.7, 0], 0.035)
        .ball([0.12, 0.9, 0], [0.18, 0.16, 0.22]);
      for (const x of [0.28, 0.44, 0.6, 0.76]) b.rib(x, 0.86, 0.28, 0.052);
      b.pair((s) => { b.leg([[0.15, 0.86, s * 0.2], [0.2, 0.45, s * 0.22], [0.15, 0.06, s * 0.22]], 0.095); b.leg([[0.86, 0.9, s * 0.21], [0.9, 0.45, s * 0.23], [0.86, 0.06, s * 0.23]], 0.095); });
    },
    skull: (b) => {
      b.ball([1.2, 1.14, 0], [0.26, 0.28, 0.23]).ball([1.3, 0.86, 0], [0.13, 0.1, 0.13]).eyes(1.36, 1.12, 0.17, 0.045)
        .pair((s) => { b.arc([1.52, 1.0, s * 0.15], 0.3, 0.05, -2.5, Math.PI * 0.95); });
    },
  },
};
/** Left to right on a plinth: the tail or a wing, then the torso, then the skull. */
export const SKELETON_ORDER: Readonly<Record<Part, number>> = { tail: 0, wing: 0, torso: 1, legs: 1, single: 1, skull: 2 };
/** Which tile of its set's plinth a part stands on (0 = the left end), as the museum lays the parts out. */
export function skeletonSlot(entry: FossilEntry, pool: readonly FossilEntry[] = itemsOfKind('fossil')): number {
  const parts = pool.filter((f) => f.set === entry.set).sort((a, b) => SKELETON_ORDER[a.part] - SKELETON_ORDER[b.part] || a.hallOrder - b.hallOrder);
  return Math.max(0, parts.findIndex((f) => f.id === entry.id));
}
function skeletonPart(entry: FossilEntry): THREE.Object3D | null {
  const draw = SKELETONS[entry.set]?.[entry.part];
  if (!draw) return null;
  const b = new Bones();
  draw(b);
  b.g.name = 'skeleton';
  b.g.position.x = -(skeletonSlot(entry) + 0.5);
  return b.g;
}

export function silhouetteShapes(id: string): THREE.Shape[] {
  return (SILHOUETTES[id] ?? SILHOUETTES.blob!)().map((p) => new THREE.Shape(p.map(([x, y]) => new THREE.Vector2(x, y))));
}

function lump(): THREE.Object3D {
  const g = new THREE.Group();
  g.name = 'lump';
  const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 1), toyMaterial(ROCK));
  rock.scale.set(1.1, 0.75, 0.9);
  rock.position.y = 0.15;
  const hint = new THREE.Mesh(new THREE.ExtrudeGeometry(silhouetteShapes('tail_long'), { depth: 0.02, bevelEnabled: false }), toyMaterial(BONE));
  hint.scale.setScalar(0.22);
  hint.position.set(0, 0.2, 0.17);
  for (const m of [rock, hint]) { m.castShadow = true; m.receiveShadow = true; }
  g.add(rock, hint);
  return g;
}

export const builder: CodeBuilder = {
  id: 'fossil',
  build(params) {
    const id = String(params.catalogId ?? '');
    const g = new THREE.Group();
    g.name = `fossil:${id}`;
    if (id === UNKNOWN_FOSSIL) { g.add(lump()); return g; }
    const entry = getEntry(id);
    const sil = entry?.kind === 'fossil' ? entry.silhouette : 'blob';
    g.userData.silhouette = sil;
    if (params.display === 'skeleton') {
      const part = entry?.kind === 'fossil' ? skeletonPart(entry) : null;
      if (part) { g.add(part); return g; }
      const geo = new THREE.ExtrudeGeometry(silhouetteShapes(sil), { depth: 0.08, bevelEnabled: true, bevelSize: 0.01, bevelThickness: 0.01, bevelSegments: 1, curveSegments: 6 });
      geo.translate(0, 0, -0.04);
      const bone = new THREE.Mesh(geo, toyMaterial(BONE));
      bone.name = 'bone';
      bone.scale.set(1.25, 1.25, 1);
      bone.castShadow = bone.receiveShadow = true;
      g.add(bone);
      return g;
    }
    const slab = new THREE.Mesh(new RoundedBoxGeometry(0.62, 0.46, 0.1, 2, 0.04), toyMaterial(STONE));
    slab.name = 'slab';
    slab.position.y = 0.23;
    const relief = new THREE.Mesh(new THREE.ExtrudeGeometry(silhouetteShapes(sil), { depth: 0.03, bevelEnabled: true, bevelSize: 0.006, bevelThickness: 0.006, bevelSegments: 1, curveSegments: 6 }), toyMaterial(BONE));
    relief.name = 'relief';
    relief.scale.set(0.5, 0.36, 1);
    relief.position.set(0, 0.23, 0.05);
    for (const m of [slab, relief]) { m.castShadow = true; m.receiveShadow = true; }
    g.add(slab, relief);
    return g;
  },
};
